import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ChevronDown,
    DollarSign,
    Download,
    MessageSquarePlus,
    Trash2,
    UserPlus,
    X,
} from "lucide-react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useClickOutsideBlock } from "../../../helpers/useClickOutside";
import { validateEmail } from "../../../helpers/management/vendors";

const INITIAL_TERMS = {
    depositFirst: 0,
    depositSecond: 0,
    balance: 0,
    credit: 0,
    depositFirstDays: 0,
    depositSecondDays: 0,
    balanceDays: 0,
    creditDays: 0,
    shippingDays: 0,
};

const PAYMENT_FIELDS = [
    { key: "depositFirst", label: "D1" },
    { key: "depositSecond", label: "D2" },
    { key: "balance", label: "Bal" },
    { key: "credit", label: "Cr" },
];

const NET_FIELDS = [
    { key: "depositFirstDays", label: "D1" },
    { key: "depositSecondDays", label: "D2" },
    { key: "balanceDays", label: "Bal" },
    { key: "creditDays", label: "Cr" },
    { key: "shippingDays", label: "Ship" },
];

function BulkPopover({ children, className = "" }) {
    return (
        <div
            className={`absolute top-full left-0 mt-1.5 bg-white border border-[oklch(0.88_0.004_260)] rounded-lg shadow-xl p-3 min-w-[320px] z-50 ${className}`}
        >
            {children}
        </div>
    );
}

function TermInput({ label, suffix, value, onChange }) {
    return (
        <div className="space-y-1">
            <label className="text-[10px] font-medium text-[oklch(0.50_0.01_260)] uppercase tracking-wide">
                {label}
            </label>
            <div className="flex items-center">
                <input
                    type="number"
                    min="0"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="border-input w-full text-primary no-arrows-by-input min-w-0 rounded-md border bg-transparent px-2 py-1 shadow-xs outline-none h-7 text-[12px] rounded-r-none text-center"
                />
                <span className="h-7 flex items-center px-1.5 bg-[oklch(0.96_0.002_260)] border border-l-0 border-[oklch(0.88_0.004_260)] rounded-r-md text-[10px] text-[oklch(0.50_0.01_260)]">
                    {suffix}
                </span>
            </div>
        </div>
    );
}

function BulkDeleteConfirm({ count, busy, onCancel, onDelete }) {
    return (
        <BulkPopover className="!w-[290px]">
            <div className="mb-3 flex items-center gap-2">
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-red-500" aria-hidden />
                <p className="text-sm font-semibold text-gray-900">
                    Delete {count} {count === 1 ? "vendor" : "vendors"}?
                </p>
            </div>
            <p className="mb-4 text-xs text-gray-500">
                This will permanently remove the selected vendor{count === 1 ? "" : "s"} from the list.
            </p>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    disabled={busy}
                    className="inline-flex h-10 flex-1 items-center justify-center rounded-md bg-gray-100 px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-50"
                    onClick={onCancel}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    disabled={busy}
                    className="inline-flex h-10 flex-1 items-center justify-center rounded-md bg-red-500 px-3 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                    onClick={onDelete}
                >
                    Delete
                </button>
            </div>
        </BulkPopover>
    );
}

function DropdownButton({ icon: Icon, label, isOpen, onClick, danger = false }) {
    const buttonClass = danger
        ? "bg-[oklch(0.22_0_0)] border-[oklch(0.35_0.1_25)] text-red-300 hover:bg-[oklch(0.28_0.05_25)]"
        : "bg-[oklch(0.22_0_0)] border-[oklch(0.3_0_0)] text-white hover:bg-[oklch(0.28_0_0)]";

    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-medium border transition-colors ${buttonClass}`}
        >
            <Icon className="w-3.5 h-3.5" />
            {label}
            {label !== "Export" && <ChevronDown className="w-3 h-3 ml-0.5 opacity-60" />}
        </button>
    );
}

const VendorBulkActionBar = ({
    selectedCount,
    busy = false,
    onDeselectAll,
    onApplyTerms,
    onAddNote,
    onAddTeamEmail,
    onExport,
    onBulkDelete,
}) => {
    const barRef = useRef(null);
    const [openMenu, setOpenMenu] = useState(null);
    const [terms, setTerms] = useState(INITIAL_TERMS);
    const [note, setNote] = useState("");
    const [teamEmail, setTeamEmail] = useState("");

    const paymentTotal = useMemo(
        () => PAYMENT_FIELDS.reduce((sum, { key }) => sum + (Number(terms[key]) || 0), 0),
        [terms]
    );

    const closeMenu = useCallback(() => setOpenMenu(null), []);

    useClickOutsideBlock(barRef, () => {
        if (openMenu) closeMenu();
    });

    useEffect(() => {
        if (!selectedCount) {
            setOpenMenu(null);
            setTerms(INITIAL_TERMS);
            setNote("");
            setTeamEmail("");
        }
    }, [selectedCount]);

    const handleTermsChange = (key, value) => {
        setTerms((prev) => ({ ...prev, [key]: value === "" ? "" : Number(value) }));
    };

    const toggleMenu = (menu) => {
        setOpenMenu((current) => (current === menu ? null : menu));
    };

    if (!selectedCount) return null;

    return (
        <div
            ref={barRef}
            className="flex items-center flex-wrap py-3 bg-[oklch(0.145_0_0)] text-white px-4 gap-2"
        >
            <button
                type="button"
                onClick={onDeselectAll}
                className="text-[13px] font-medium whitespace-nowrap hover:underline"
            >
                {selectedCount} selected
            </button>
            <div className="w-px h-5 bg-white/15 mx-1" />
            <div className="flex items-center flex-wrap gap-1.5">
                <div className="relative">
                    <DropdownButton
                        icon={DollarSign}
                        label="Edit Terms"
                        isOpen={openMenu === "terms"}
                        onClick={() => toggleMenu("terms")}
                    />
                    {openMenu === "terms" && (
                        <BulkPopover>
                            <div className="space-y-4">
                                <p className="text-[12px] font-medium text-[oklch(0.13_0.005_260)]">
                                    Set terms for {selectedCount} vendor(s)
                                </p>
                                <div className="space-y-2">
                                    <p className="text-[11px] font-semibold text-[oklch(0.50_0.01_260)] uppercase tracking-wide">
                                        Payment Terms
                                    </p>
                                    <div className="grid grid-cols-4 gap-2">
                                        {PAYMENT_FIELDS.map(({ key, label }) => (
                                            <TermInput
                                                key={key}
                                                label={label}
                                                suffix="%"
                                                value={terms[key]}
                                                onChange={(value) => handleTermsChange(key, value)}
                                            />
                                        ))}
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className={`text-[11px] font-medium ${paymentTotal === 100 ? "text-[oklch(0.50_0.01_260)]" : "text-red-500"}`}>
                                            Total: {paymentTotal}%
                                        </span>
                                    </div>
                                </div>
                                <div className="h-px bg-[oklch(0.88_0.004_260)]" />
                                <div className="space-y-2">
                                    <p className="text-[11px] font-semibold text-[oklch(0.50_0.01_260)] uppercase tracking-wide">
                                        Net Terms (days)
                                    </p>
                                    <div className="grid grid-cols-5 gap-2">
                                        {NET_FIELDS.map(({ key, label }) => (
                                            <TermInput
                                                key={key}
                                                label={label}
                                                suffix="d"
                                                value={terms[key]}
                                                onChange={(value) => handleTermsChange(key, value)}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => {
                                        onApplyTerms(terms);
                                        closeMenu();
                                    }}
                                    className="w-full h-8 rounded-md bg-[oklch(0.13_0.005_260)] text-white text-[13px] font-medium hover:bg-[oklch(0.2_0.005_260)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Apply
                                </button>
                            </div>
                        </BulkPopover>
                    )}
                </div>

                <div className="relative">
                    <DropdownButton
                        icon={MessageSquarePlus}
                        label="Quick Note"
                        isOpen={openMenu === "note"}
                        onClick={() => toggleMenu("note")}
                    />
                    {openMenu === "note" && (
                        <BulkPopover>
                            <div className="space-y-3">
                                <p className="text-[12px] font-medium text-[oklch(0.13_0.005_260)]">
                                    Add note to {selectedCount} vendor(s)
                                </p>
                                <textarea
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="Type a quick note..."
                                    rows={3}
                                    className="w-full px-3 py-2 text-[13px] border border-[oklch(0.88_0.004_260)] rounded-md bg-white resize-none focus:outline-none focus:ring-2 focus:ring-[oklch(0.55_0.15_260)] focus:border-transparent text-[oklch(0.13_0.005_260)] placeholder:text-[oklch(0.50_0.01_260)]"
                                    onKeyDown={(e) => {
                                        if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && note.trim()) {
                                            e.preventDefault();
                                            onAddNote(note.trim());
                                            setNote("");
                                            closeMenu();
                                        }
                                    }}
                                />
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] text-[oklch(0.50_0.01_260)]">Cmd+Enter to apply</span>
                                    <button
                                        type="button"
                                        disabled={busy || !note.trim()}
                                        onClick={() => {
                                            onAddNote(note.trim());
                                            setNote("");
                                            closeMenu();
                                        }}
                                        className="h-8 px-4 rounded-md bg-[oklch(0.13_0.005_260)] text-white text-[13px] font-medium hover:bg-[oklch(0.2_0.005_260)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        Add Note
                                    </button>
                                </div>
                            </div>
                        </BulkPopover>
                    )}
                </div>

                <div className="relative">
                    <DropdownButton
                        icon={UserPlus}
                        label="Add Team Email"
                        isOpen={openMenu === "teamEmail"}
                        onClick={() => toggleMenu("teamEmail")}
                    />
                    {openMenu === "teamEmail" && (
                        <BulkPopover>
                            <div className="space-y-3">
                                <p className="text-[12px] font-medium text-[oklch(0.13_0.005_260)]">
                                    Add team email to {selectedCount} vendor(s)
                                </p>
                                <input
                                    type="email"
                                    value={teamEmail}
                                    onChange={(e) => setTeamEmail(e.target.value)}
                                    placeholder="email@kingsloot.com"
                                    className="border-input w-full min-w-0 text-primary rounded-md border bg-transparent px-3 py-1 shadow-xs outline-none h-8 text-[13px] focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
                                />
                                <button
                                    type="button"
                                    disabled={busy || !validateEmail(teamEmail.trim())}
                                    onClick={() => {
                                        onAddTeamEmail(teamEmail.trim());
                                        setTeamEmail("");
                                        closeMenu();
                                    }}
                                    className="w-full h-8 rounded-md bg-[oklch(0.13_0.005_260)] text-white text-[13px] font-medium hover:bg-[oklch(0.2_0.005_260)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Apply
                                </button>
                            </div>
                        </BulkPopover>
                    )}
                </div>

                <button
                    type="button"
                    disabled={busy}
                    onClick={onExport}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-medium border transition-colors bg-[oklch(0.22_0_0)] border-[oklch(0.3_0_0)] text-white hover:bg-[oklch(0.28_0_0)] disabled:opacity-50"
                >
                    <Download className="w-3.5 h-3.5" />
                    Export
                </button>

                <div className="relative">
                    <DropdownButton
                        icon={Trash2}
                        label="Delete"
                        isOpen={openMenu === "delete"}
                        onClick={() => toggleMenu("delete")}
                        danger
                    />
                    {openMenu === "delete" && (
                        <BulkDeleteConfirm
                            count={selectedCount}
                            busy={busy}
                            onCancel={closeMenu}
                            onDelete={() => {
                                onBulkDelete();
                                closeMenu();
                            }}
                        />
                    )}
                </div>
            </div>
            <div className="flex-1" />
            <button
                type="button"
                onClick={onDeselectAll}
                className="p-1.5 rounded-md hover:bg-white/10 transition-colors"
                title="Deselect all"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
};

export default VendorBulkActionBar;
